"use client";
import { AUTH_API_POST_CONTENT_TYPE } from "./constants";
import { navigateTo, generateRandomString, pkceChallengeFromVerifier, clearUrlFromKeycloakState } from "./helpers";
import { realm, pkceState, logoutTimer, realmConfig, accessToken, refreshTimer, openIdConfig, refreshToken, accessTokenExp, refreshTokenExp, pkceCodeVerifier, } from "./store";
export const setAccessToken = (value) => {
    accessToken.set(value);
};
export const setAccessTokenExp = (value) => {
    accessTokenExp.set(value);
};
export const setRefreshToken = (value) => {
    refreshToken.set(value);
};
export const setRefreshTokenExp = (value) => {
    refreshTokenExp.set(value);
};
export const setOpenIdConfig = (config) => {
    openIdConfig.set(config);
};
export const getOpenIdConfigStore = () => {
    return openIdConfig.get();
};
export const setRealmStorage = (realmValue) => {
    realm.set(realmValue);
};
export const getRealmStorage = () => {
    return realm.get();
};
export const setRealmConfig = (config) => {
    realmConfig.set(config);
};
export const getRealmConfig = () => {
    return realmConfig.get();
};
export const getRefreshToken = () => {
    return refreshToken.get();
};
export const setPkceState = (state) => {
    pkceState.set(state);
};
export const getPkceState = () => {
    return pkceState.get();
};
export const setPkceCodeVerifier = (codeVerifier) => {
    pkceCodeVerifier.set(codeVerifier);
};
export const getPkceCodeVerifier = () => {
    return pkceCodeVerifier.get();
};
export const setRefreshTimer = (timerLifespan) => {
    refreshTimer.set(setTimeout(() => {
        refreshAccessToken()
            // Send to login screen in case of error (like 400)
            .catch(() => {
            resetState();
        });
    }, timerLifespan));
};
export const setLogoutTimer = (timerLifespan) => {
    logoutTimer.set(setTimeout(logout, timerLifespan));
};
export const getLogoutTimer = () => {
    return logoutTimer.get();
};
export const getRefreshTimer = () => {
    return refreshTimer.get();
};
export const fetchOpenIdConfig = (openidConfigUrl) => {
    return fetch(openidConfigUrl)
        .then((response) => response.json())
        .then((result) => {
        setOpenIdConfig(result);
        return result;
    });
};
export const fetchAndStoreAccessToken = (code, scope) => {
    const realmConf = getRealmConfig();
    const openIdConf = getOpenIdConfigStore();
    const bodyParams = new URLSearchParams();
    const redirectUrl = clearUrlFromKeycloakState(window.location.href);
    if (openIdConf === null || realmConf === null) {
        return Promise.resolve();
    }
    if (scope !== undefined) {
        bodyParams.append("scope", scope.join(" "));
    }
    bodyParams.append("code", code);
    bodyParams.append("client_id", realmConf.clientId);
    bodyParams.append("redirect_uri", redirectUrl);
    bodyParams.append("grant_type", "authorization_code");
    bodyParams.append("code_verifier", getPkceCodeVerifier());
    return fetch(openIdConf.token_endpoint, {
        method: "POST",
        headers: {
            "Content-Type": AUTH_API_POST_CONTENT_TYPE,
        },
        body: bodyParams.toString(),
    })
        .then((response) => {
        if (!response.ok) {
            return Promise.reject(response);
        }
        return response;
    })
        .then((response) => response.json())
        .then((data) => {
        setTokens(data);
        window.history.replaceState(null, "", clearUrlFromKeycloakState(window.location.href));
        return data;
    })
        .catch((error) => {
        resetState();
        throw error;
    });
};
export const refreshAccessToken = () => {
    const realmConf = getRealmConfig();
    const openIdConf = getOpenIdConfigStore();
    const bodyParams = new URLSearchParams();
    const redirectUrl = clearUrlFromKeycloakState(window.location.href);
    if (openIdConf === null || realmConf === null) {
        return Promise.resolve();
    }
    bodyParams.append("client_id", realmConf.clientId);
    bodyParams.append("redirect_uri", redirectUrl);
    bodyParams.append("grant_type", "refresh_token");
    bodyParams.append("refresh_token", getRefreshToken());
    return fetch(openIdConf.token_endpoint, {
        body: bodyParams.toString(),
        method: "POST",
        headers: {
            "Content-Type": AUTH_API_POST_CONTENT_TYPE,
        },
    })
        .then((response) => {
        if (!response.ok) {
            return Promise.reject(response);
        }
        return response;
    })
        .then((response) => response.json())
        .then((data) => {
        setTokens(data);
        return data;
    });
};
export const resetState = () => {
    setOpenIdConfig(null);
    setAccessToken("");
    setAccessTokenExp("");
    setPkceCodeVerifier("");
    setPkceState("");
    setRefreshToken("");
    setRefreshTokenExp("");
    refreshTimer.set(0);
    logoutTimer.set(0);
    setRealmConfig(null);
};
export const setTokens = (tokenData) => {
    setAccessToken(tokenData.access_token);
    setAccessTokenExp(String(Date.now() + tokenData.expires_in * 1000));
    setRefreshToken(tokenData.refresh_token);
    setRefreshTokenExp(String(Date.now() + tokenData.refresh_expires_in * 1000));
};
export const logout = () => {
    const openIdConf = getOpenIdConfigStore();
    if (openIdConf === null) {
        return;
    }
    const url = new URL(openIdConf?.end_session_endpoint);
    url.searchParams.append("redirect_uri", encodeURI(window.location.origin));
    localStorage.clear();
    location.replace(url.toString());
};
export const redirectToAuthUrl = (authorizationEndpoint, clientId) => {
    const redirectUrl = clearUrlFromKeycloakState(window.location.href);
    // Start the procedure of new authentication
    const state = generateRandomString();
    // Create and store a new PKCE code_verifier (the plaintext random secret)
    const codeVerifier = generateRandomString();
    // Hash and base64-urlencode the secret to use as the challenge
    pkceChallengeFromVerifier(codeVerifier).then((codeChallenge) => {
        // Build the authentication URL
        if (authorizationEndpoint) {
            const authURL = new URL(authorizationEndpoint);
            authURL.searchParams.append("state", state);
            authURL.searchParams.append("client_id", clientId);
            authURL.searchParams.append("response_type", "code");
            authURL.searchParams.append("redirect_uri", redirectUrl);
            authURL.searchParams.append("code_challenge", codeChallenge);
            authURL.searchParams.append("code_challenge_method", "S256");
            setPkceState(state);
            setPkceCodeVerifier(codeVerifier);
            // Redirect to authentication URL
            navigateTo(authURL.toString());
        }
    });
};
export const handleTokenRefreshTimer = (accessTokenExpiration) => {
    // Clearing timer if it's already set, so we'll have only one timer running
    if (getRefreshTimer()) {
        clearTimeout(getRefreshTimer());
    }
    // If user has an access token, then we just setup a timer to refresh it later
    // Timer is set for 95% so the token can be refreshed before actually expiring
    const timerLifespan = (accessTokenExpiration - Date.now()) * 0.95;
    setRefreshTimer(timerLifespan);
};
export const handleLogoutTimer = (refreshTokenExpValue) => {
    // Clearing timer if it's already set, so we'll have only one timer running
    if (getLogoutTimer()) {
        clearTimeout(getLogoutTimer());
    }
    // Setting a timer to perform a hard logout when refresh token expires
    // Timer is set for 95% so the token can be refreshed before actually expiring
    const timerLifespan = (refreshTokenExpValue - Date.now()) * 0.95;
    setLogoutTimer(timerLifespan);
};
//# sourceMappingURL=actions.js.map